import { Table } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { fetchPendingEmployeeRequests } from "../../../../services/EmployeeDetailServices";
import { encrypt } from "../../../../utils/crypto";
import TooltipWrapper from "../../../../utils/TooltipWrapper";

const EmployeeRequestList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 10,
    search: null,
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });

  const fetchEmployeeRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: filters.pageSize,
      };

      if (filters.search) {
        params.search = filters.search;
      } 
      const response = await fetchPendingEmployeeRequests(params); 

      if (response) {
        // Handle the data array
        const employeeData = response.data || []; 
        setEmployees(employeeData);
        
        // Handle pagination response structure
        if (response.pagination) { 
          // If API returns total as 0 but we have data, use the data length as a fallback
          const apiTotal = response.pagination.total || 0;
          const actualTotal = apiTotal > 0 ? apiTotal : employeeData.length;
          const totalPages = response.pagination.totalPages || Math.ceil(actualTotal / filters.pageSize) || 1;
          
          setPagination({
            current_page: filters.page,
            per_page: filters.pageSize,
            total: actualTotal,
            total_pages: totalPages
          });
        } else {
          // Fallback for different response structure
          const total = response.total || response.count || employeeData.length; 
          setPagination({
            current_page: filters.page,
            per_page: filters.pageSize,
            total: total,
            total_pages: Math.ceil(total / filters.pageSize) || 1
          });
        }
      } else { 
        setEmployees([]);
      }
    } catch (error) { 
      toast.error(t("Failed to fetch employee requests"));
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchEmployeeRequests();
  }, [fetchEmployeeRequests]);

  // Debounce search term
  useEffect(() => {
    const delayTimeout = setTimeout(() => {
      updateFilters({
        search: searchTerm.trim() || null,
      });
    }, 500); // 500ms debounce delay

    return () => clearTimeout(delayTimeout);
  }, [searchTerm]);

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page when filters change
    }));
  };

  const updatePagination = (page, pageSize) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        page: parseInt(page) || 1,
        pageSize: pageSize || prev.pageSize,
      };
      return newFilters;
    });
  };

  const handleView = (record) => {
    if (!record?.employee_id) return;
    const encryptedId = encrypt(record.employee_id.toString());
    navigate(`${encryptedId}`);
  };

  const columns = [
    {
      title: t("S.No"),
      key: "serial",
      width: 80,
      render: (_, __, index) => (
        <span>{(pagination.current_page - 1) * pagination.per_page + index + 1}</span>
      ),
    },
    {
      title: t("Employee Name"),
      dataIndex: "first_name",
      key: "first_name",
      render: (text, record) => (
        <div>
          <div className="fw-semibold">
            {record?.first_name} {record?.last_name}
          </div>
          <div className="text-muted small">{record?.official_email}</div>
        </div>
      ),
    },
    {
      title: t("Personal Email"),
      dataIndex: "personal_email",
      key: "personal_email",
    },
    {
      title: t("Department"),
      dataIndex: "department_name",
      key: "department_name",
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div className="btn-group" role="group">
          <TooltipWrapper tooltipText={t("View Details")}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleView(record)}
              type="button"
            >
              <i className="fa fa-eye" />
            </button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Pending Requests")}
          title={t("Dashboard")}
          subtitle={t("Pending Requests")}
        />

        <Card>
          <Card.Header className="px-3 pt-3 py-0">
            <div
              className="d-flex flex-md-nowrap flex-wrap justify-content-between align-items-center gap-1 "
              style={{ minHeight: "40px" }}
            >
              <div
                className="d-flex align-items-center gap-2 flex-grow-1"
                style={{ minWidth: 0, overflow: "hidden" }}
              >
                <InputGroup
                  className="flex-shrink-1"
                  style={{
                    width: "clamp(200px, 50vw, 20rem)",
                    maxWidth: "100%",
                  }}
                >
                  <Form.Control
                    className="form-control"
                    placeholder={t("Search by name, email, department")}
                    style={{
                      height: "2.4rem",
                    }}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                  />
                  <Button variant="primary" disabled>
                    <i className="fa fa-search" />
                  </Button>
                </InputGroup>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-2 p-md-3">
            <div className="table-responsive">
              <Table
                className="table-striped"
                columns={columns}
                dataSource={employees}
                loading={loading}
                rowKey={(record) => record.employee_id}
                pagination={false}
              />
            </div>

            {/* Custom Pagination Outside Table - Left and Right Sides */}
            <div
              className="d-flex justify-content-between align-items-center mt-3 px-3 py-2"
              style={{
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 8px 8px",
                minHeight: "50px",
              }}
            >
              {/* LEFT SIDE - Total count */}
              <div className="d-flex align-items-center">
                <div
                  className="pagination-info-box"
                  style={{
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    color: "#666",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    fontWeight: "500",
                  }}
                >
                  {pagination.total > 0
                    ? `${
                        (pagination.current_page - 1) * pagination.per_page + 1
                      }-${Math.min(
                        pagination.current_page * pagination.per_page,
                        pagination.total
                      )} of ${pagination.total} employees`
                    : "0 employees"}
                </div>
              </div>

              {/* RIGHT SIDE - Pagination controls */}
              <div className="d-flex align-items-center gap-3">
                {/* Page size selector */}
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {pagination.per_page} / page
                  </span>

                  <select
                    className="form-select form-select-sm"
                    value={pagination.per_page}
                    onChange={(e) =>
                      updatePagination(1, parseInt(e.target.value))
                    }
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "6px 10px",
                      fontSize: "14px",
                      width: "110px",
                      height: "32px",
                    }}
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="d-flex align-items-center gap-1">
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                    disabled={pagination.current_page <= 1}
                    onClick={() =>
                      updatePagination(
                        pagination.current_page - 1,
                        pagination.per_page
                      )
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  >
                    <i
                      className="fa fa-chevron-left"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </button>

                  {Array.from(
                    { length: Math.min(5, pagination.total_pages) },
                    (_, i) => {
                      const startPage = Math.max(
                        1,
                        pagination.current_page - 2
                      );
                      const pageNum = startPage + i;

                      if (pageNum > pagination.total_pages) return null;

                      return (
                        <button
                          key={pageNum}
                          className={`btn btn-sm d-flex align-items-center justify-content-center ${
                            pageNum === pagination.current_page
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() =>
                            updatePagination(pageNum, pagination.per_page)
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            fontSize: "14px",
                            fontWeight:
                              pageNum === pagination.current_page
                                ? "600"
                                : "400",
                            border: "1px solid #d9d9d9",
                            borderRadius: "4px",
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                    disabled={pagination.current_page >= pagination.total_pages}
                    onClick={() =>
                      updatePagination(
                        pagination.current_page + 1,
                        pagination.per_page
                      )
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  >
                    <i
                      className="fa fa-chevron-right"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeRequestList;
